(() => {
  if (window.opticWhiteAutoplayReady) return;
  window.opticWhiteAutoplayReady = true;
  const players = new Map();
  const isVisible = (video) => {
    const rect = video.getBoundingClientRect();
    return !document.hidden && rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < innerHeight && rect.right > 0 && rect.left < innerWidth;
  };

  class AmbientVideo {
    constructor(video) {
      this.video = video;
      this.handlers = [];
      this.recoveries = 0;
      this.playTicket = 0;
      video.muted = true;
      video.defaultMuted = true;
      video.autoplay = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = 'auto';
      this.on('loadedmetadata', () => {
        if (this.resumeTime != null) {
          try { video.currentTime = Math.min(this.resumeTime, Math.max(0, video.duration - 0.1)); } catch {}
          this.resumeTime = null;
        }
      });
      for (const name of ['loadeddata', 'canplay']) this.on(name, () => this.attempt());
      this.on('playing', () => { this.blocked = false; this.watch(); });
      this.on('timeupdate', () => {
        if (Math.abs(video.currentTime - (this.progressTime || 0)) > 0.1) {
          this.progressTime = video.currentTime;
          this.recoveries = 0;
        }
      });
      this.on('pause', () => {
        clearTimeout(this.retryTimer);
        if (isVisible(video) && !this.blocked) this.retryTimer = setTimeout(() => this.attempt(), 150);
      });
      this.on('ended', () => {
        if (!isVisible(video)) return;
        try { video.currentTime = 0; } catch {}
        this.attempt();
      });
      this.on('error', () => {
        if (video.error?.code === 2) this.recover();
      });
      this.observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(() => this.resume(), { threshold: 0.01 }) : null;
      this.observer?.observe(video);
      this.attempt();
      this.watch();
    }

    on(name, handler) {
      this.video.addEventListener(name, handler);
      this.handlers.push([name, handler]);
    }

    attempt(gesture = false) {
      const video = this.video;
      if (!video.isConnected || !isVisible(video) || (this.blocked && !gesture) || this.pending || !video.paused) return;
      if (video.error) return;
      video.muted = true;
      const ticket = ++this.playTicket;
      this.pending = true;
      try {
        const result = video.play();
        Promise.resolve(result).catch((error) => {
          if (ticket !== this.playTicket) return;
          if (error.name === 'NotAllowedError') this.blocked = true;
        }).finally(() => {
          if (ticket === this.playTicket) this.pending = false;
        });
      } catch {
        this.pending = false;
      }
    }

    resume(gesture = false) {
      if (!isVisible(this.video)) {
        clearTimeout(this.watchTimer);
        return;
      }
      if (gesture) this.blocked = false;
      if (this.video.error?.code === 2) this.recover();
      this.attempt(gesture);
      this.watch();
    }

    watch() {
      clearTimeout(this.watchTimer);
      if (!this.video.isConnected || !isVisible(this.video) || this.blocked || (this.video.error && this.video.error.code !== 2)) return;
      const position = this.video.currentTime;
      this.watchTimer = setTimeout(() => {
        if (!isVisible(this.video) || this.blocked) return;
        const progressing = Math.abs(this.video.currentTime - position) > 0.05;
        if (!progressing && this.video.readyState < 3 && this.video.networkState !== 3) this.recover();
        else if (!progressing && !this.video.paused && this.video.readyState >= 3) {
          this.video.pause();
          this.attempt();
        } else this.attempt();
        this.watch();
      }, 8000);
    }

    recover() {
      if (!this.video.isConnected || !isVisible(this.video) || this.blocked || this.recoveries >= 2) return;
      if (this.video.error && this.video.error.code !== 2) return;
      this.recoveries++;
      if (this.resumeTime == null) this.resumeTime = this.video.currentTime;
      this.playTicket++;
      this.pending = false;
      try { this.video.load(); } catch {}
    }

    destroy() {
      this.playTicket++;
      clearTimeout(this.retryTimer);
      clearTimeout(this.watchTimer);
      this.observer?.disconnect();
      this.handlers.forEach(([name, handler]) => this.video.removeEventListener(name, handler));
    }
  }

  function discover(root) {
    if (root.nodeType !== 1 && root.nodeType !== 9) return;
    const found = root.matches?.('video[data-optic-autoplay]') ? [root] : [];
    found.push(...root.querySelectorAll('video[data-optic-autoplay]'));
    found.forEach((video) => { if (!players.has(video)) players.set(video, new AmbientVideo(video)); });
  }

  const resume = () => players.forEach((player) => player.resume());
  const gesture = () => players.forEach((player) => player.resume(true));
  function init() {
    discover(document);
    const observer = new MutationObserver((records) => {
      records.forEach((record) => record.addedNodes.forEach(discover));
      players.forEach((player, video) => {
        if (!video.isConnected) { player.destroy(); players.delete(video); }
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
  document.addEventListener('shopify:section:load', (event) => discover(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    players.forEach((player, video) => {
      if (event.target.contains(video)) { player.destroy(); players.delete(video); }
    });
  });
  document.addEventListener('visibilitychange', resume);
  window.addEventListener('pageshow', resume);
  window.addEventListener('focus', resume);
  window.addEventListener('online', () => {
    players.forEach((player) => { player.recoveries = 0; player.resume(); });
  });
  document.addEventListener('click', gesture, { capture: true, passive: true });
  document.addEventListener('touchend', gesture, { capture: true, passive: true });
  document.addEventListener('keydown', gesture, { capture: true });
  if (typeof IntersectionObserver !== 'function') {
    let scheduled = false;
    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => { scheduled = false; resume(); });
    };
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
